import React, { useEffect, useMemo, useState } from 'react';
import FishIQSpeciesImage from './FishIQSpeciesImage';

interface FishIQSpeciesCardProps {
  species: any;
  onOpenSummary?: () => void;
  onToggleSaved?: () => void;
  isSaved?: boolean;
  isDarkMode?: boolean;
  isActive?: boolean;
}

interface FishIQSpeciesSummaryCardProps {
  species: any;
  onOpenDetail?: () => void;
  onClose?: () => void;
  isDarkMode?: boolean;
}

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

const fishIQPremiumMicroStyles = `
@keyframes roddoFishIQPremiumMicroIn {
  from { opacity: 0; transform: translateY(5px) scale(0.995); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.roddo-fishiq-premium-micro-in {
  animation: roddoFishIQPremiumMicroIn 180ms cubic-bezier(0.22, 1, 0.36, 1) both;
}
@media (prefers-reduced-motion: reduce) {
  .roddo-fishiq-premium-micro-in { animation: none !important; }
}
`;

const uniqueList = (input: any, limit = 4): string[] => {
  const src = Array.isArray(input)
    ? input
    : typeof input === 'string'
      ? input.split(/\n|,|•|·/g)
      : [];

  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of src) {
    const value = String(item ?? '').trim();
    if (!value) continue;
    const key = value.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(value);
    if (out.length >= limit) break;
  }
  return out;
};

const shortText = (value: any, max = 170) => {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
};

const titleCase = (value: any) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const speciesIconFor = (species: any, waterBits: string[]) => {
  const haystack = [
    species?.commonName,
    species?.name,
    species?.scientificName,
    species?.family,
    ...(Array.isArray(species?.aliases) ? species.aliases : []),
    ...waterBits,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/(crab|lobster|shrimp|prawn|crawfish|crayfish)/.test(haystack)) return '🦀';
  if (/(clam|oyster|mussel|scallop|abalone)/.test(haystack)) return '🦪';
  if (/(squid|octopus|cephalopod)/.test(haystack)) return '🦑';
  if (/(urchin|sea urchin|uni)/.test(haystack)) return '🪸';
  if (/(crappie|bluegill|sunfish|perch|tilapia)/.test(haystack)) return '🐠';
  if (/(catfish|carp|sturgeon|drum)/.test(haystack)) return '🐡';
  return '🐟';
};

const getSpeciesRead = (species: any) => {
  const commonName = species?.commonName || species?.name || 'Unknown species';
  const scientificName = String(species?.scientificName || species?.taxonomy?.scientificName || '').trim();
  const fishIQ = species?.__fishiq || {};
  const locationRank = fishIQ?.locationRank || null;

  const waterNames = uniqueList([
    locationRank?.waterLabel,
    species?.waterName,
    species?.waterType,
    species?.waterTypeKey,
    ...(Array.isArray(species?.topWaterTypes) ? species.topWaterTypes : []),
    ...(Array.isArray(species?.habitats) ? species.habitats : []),
    ...(Array.isArray(species?.waterTypes) ? species.waterTypes : []),
  ], 3);

  const habitats = uniqueList(species?.habitats || species?.waterTypes || species?.topWaterTypes, 3);
  const baits = uniqueList(species?.tactics?.bestBaits || species?.bestBaits, 3);
  const techniques = uniqueList(species?.tactics?.bestTechniques || species?.bestTechniques, 3);
  const structures = uniqueList(species?.structureTags, 4);
  const behavior = uniqueList(species?.behaviorTags, 3);
  const times = uniqueList(species?.activeTimes || species?.bestTimesOfDay || species?.tactics?.bestTimesOfDay, 3);

  const waterLabel = waterNames[0] || habitats[0] || 'Area read';
  const firstStructure = structures[0] || habitats[0] || waterLabel;
  const bestMove = shortText(fishIQ?.bestMove || locationRank?.bestMove || techniques[0] || structures[0] || behavior[0] || 'Work the most likely cover first.', 92);
  const bestThrow = shortText(fishIQ?.bestBait || locationRank?.bestBait || baits[0] || 'Match forage', 58);
  const bestWindow = shortText(locationRank?.bestWindow || fishIQ?.bestWindow || times[0] || '', 58);
  const whyHere = shortText(fishIQ?.whyHere || locationRank?.whyHere || '', 175);
  const whyNow = shortText(fishIQ?.whyNow || locationRank?.whyNow || '', 145);
  const localReadBody = shortText(fishIQ?.localReadBody || [whyHere, whyNow].filter(Boolean).join(' '), 190);

  const summary = shortText(
    fishIQ?.summary ||
      species?.summary ||
      species?.descriptionShort ||
      species?.description ||
      species?.feedingProfile?.notes ||
      `${commonName} fits this read around ${String(firstStructure).toLowerCase()}. Start simple, make one intentional cast, and log what happened.`,
    210,
  );

  const actionLine = shortText(
    fishIQ?.actionLine ||
      locationRank?.actionLine ||
      `Try ${commonName} around ${String(firstStructure).toLowerCase()}; start with ${String(bestThrow).toLowerCase()} and log what happens.`,
    175,
  );

  return {
    commonName,
    scientificName,
    waterNames,
    habitats,
    structures,
    behavior,
    waterLabel,
    bestMove,
    bestThrow,
    bestWindow,
    localReadBody,
    summary,
    actionLine,
  };
};

const saveButtonClass = (isDarkMode: boolean, isSaved?: boolean) =>
  cx(
    'grid h-8 w-8 shrink-0 place-items-center rounded-2xl border text-base font-black transition active:scale-95 min-[390px]:h-9 min-[390px]:w-9 min-[390px]:text-lg',
    isDarkMode
      ? isSaved
        ? 'border-[#FFD977]/24 bg-[#FFD977]/16 text-[#FFE8A4]'
        : 'border-white/10 bg-white/10 text-[#EFF3FF]'
      : isSaved
        ? 'border-[#E8D28A] bg-[#FFF2C8] text-[#7A5A00]'
        : 'border-[#E4D8C9] bg-[#FFF9F0] text-[#6B5E52]',
  );


const FishIQSpeciesCard: React.FC<FishIQSpeciesCardProps> = ({
  species,
  onOpenSummary,
  onToggleSaved,
  isSaved,
  isDarkMode = false,
  isActive = false,
}) => {
  const read = getSpeciesRead(species);
  const icon = useMemo(() => speciesIconFor(species, [...read.waterNames, ...read.habitats]), [species, read.waterNames, read.habitats]);
  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpenSummary}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenSummary?.();
        }
      }}
      aria-pressed={isActive}
      className={cx(
        'roddo-fishiq-species-card group relative w-full cursor-pointer overflow-hidden rounded-[16px] border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#66D9E8]/45',
        'min-h-[72px] snap-start',
        isDarkMode
          ? isActive
            ? 'border-[#8A70F2]/32 bg-[#151F31] shadow-[0_6px_16px_rgba(0,0,0,0.15)] ring-1 ring-[#8A70F2]/12'
            : 'border-[rgba(132,151,190,0.12)] bg-[#101827] shadow-[0_4px_12px_rgba(0,0,0,0.12)] hover:bg-[#121D30]'
          : isActive
            ? 'border-[#2F80ED]/25 bg-[#2F80ED]/[0.045] shadow-[0_8px_20px_rgba(47,128,237,0.08)]'
            : 'border-[#E5D9CA] bg-[#FFFCF8] shadow-[0_6px_16px_rgba(96,72,40,0.06)] hover:bg-white',
      )}
    >
      <div className="flex items-center gap-3 p-2.5 min-[390px]:p-3">
        <FishIQSpeciesImage
          species={species}
          fallback={<span aria-hidden="true">{icon}</span>}
          size="thumb"
          className={cx(
            'h-[52px] w-[70px] shrink-0 rounded-[14px] border text-[22px] shadow-inner',
            isDarkMode ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E7DBCF] bg-white/82',
          )}
          imageClassName="object-cover"
        />

        <div className="min-w-0 flex-1 text-left">
          <div className={cx('truncate text-[16px] font-black leading-tight min-[390px]:text-[17px]', isDarkMode ? 'text-[#F4F6FA]' : 'text-[#332C25]')}>
            {read.commonName}
          </div>
          {read.scientificName ? (
            <div className={cx('mt-1 hidden truncate text-[10.5px] italic leading-4 min-[430px]:block', isDarkMode ? 'text-[#9EABBF]' : 'text-[#7A6E63]')}>
              {read.scientificName}
            </div>
          ) : null}
          <div className={cx('mt-1 truncate text-[10.5px] font-extrabold leading-4 min-[390px]:text-[11px]', isDarkMode ? 'text-[#AEB8C9]' : 'text-[#675D53]')}>
            <span className={cx('font-black', isDarkMode ? 'text-[#66D9E8]' : 'text-[#1C6B94]')}>{read.bestThrow}</span>
            {read.bestWindow ? <span> · {read.bestWindow}</span> : null}
          </div>
        </div>

        {typeof onToggleSaved === 'function' ? (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onToggleSaved();
            }}
            className={saveButtonClass(isDarkMode, isSaved)}
            aria-label={isSaved ? 'Unsave' : 'Save'}
            title={isSaved ? 'Saved' : 'Save'}
          >
            {isSaved ? '★' : '☆'}
          </button>
        ) : (
          <span className={cx('mr-1 text-lg font-black transition-transform group-hover:translate-x-0.5', isDarkMode ? 'text-white/35' : 'text-black/30')} aria-hidden="true">›</span>
        )}
      </div>
    </article>
  );
};

export const FishIQSpeciesSummaryCard: React.FC<FishIQSpeciesSummaryCardProps> = ({
  species,
  onOpenDetail,
  onClose,
  isDarkMode = false,
}) => {
  const read = getSpeciesRead(species);
  const icon = useMemo(() => speciesIconFor(species, [...read.waterNames, ...read.habitats]), [species, read.waterNames, read.habitats]);
  const [isImageExpanded, setIsImageExpanded] = useState(false);

  useEffect(() => {
    if (!isImageExpanded) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsImageExpanded(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isImageExpanded]);

  return (
    <>
      <style>{fishIQPremiumMicroStyles}</style>
    <article
      className={cx(
        'roddo-fishiq-premium-micro-in relative w-full overflow-hidden rounded-[22px] border p-3.5 transition-all duration-200',
        isDarkMode
          ? 'border-[rgba(103,216,232,0.14)] bg-[#0A1120] shadow-[0_10px_26px_rgba(0,0,0,0.28)]'
          : 'border-[#DCCFBE] bg-[#FFFDF9] shadow-[0_20px_46px_rgba(96,72,40,0.16)]',
      )}
    >
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setIsImageExpanded(true);
          }}
          className="group relative shrink-0 rounded-[16px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#66D9E8]/55"
          aria-label={`Expand ${read.commonName} reference image`}
          title="View larger image"
        >
          <FishIQSpeciesImage
            species={species}
            fallback={<span aria-hidden="true">{icon}</span>}
            size="card"
            className={cx(
              'h-[84px] w-[108px] shrink-0 rounded-[16px] border text-[28px] shadow-inner transition-transform duration-200 group-hover:scale-[1.01]',
              isDarkMode ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E7DBCF] bg-white/86',
            )}
            imageClassName="object-cover"
          />
          <span className={cx('pointer-events-none absolute bottom-1.5 right-1.5 grid h-7 w-7 place-items-center rounded-full border text-[12px] backdrop-blur-md', isDarkMode ? 'border-white/[0.08] bg-[#090B13]/82 text-white' : 'border-black/10 bg-white/90 text-[#574C43]')} aria-hidden="true">↗</span>
        </button>

        <div className="min-w-0 flex-1 pt-0.5">
          <div className={cx('text-[19px] font-black leading-tight', isDarkMode ? 'text-[#F4F6FA]' : 'text-[#332C25]')}>
            {read.commonName}
          </div>
          {read.scientificName ? (
            <div className={cx('mt-1 truncate text-[11px] italic', isDarkMode ? 'text-[#9EABBF]' : 'text-[#7A6E63]')}>
              {read.scientificName}
            </div>
          ) : null}
          <div className={cx('mt-2 text-[11px] font-bold leading-4', isDarkMode ? 'text-[#AEB8C9]' : 'text-[#6E6359]')}>
            {[titleCase(read.waterLabel), read.habitats[0] ? titleCase(read.habitats[0]) : ''].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).slice(0, 2).join(' • ')}
          </div>
        </div>

        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            className={cx(
              'grid h-8 w-8 shrink-0 place-items-center rounded-full border text-[12px] font-black transition active:scale-95',
              isDarkMode ? 'border-white/10 bg-white/[0.06] text-[#EDF2FF]' : 'border-[#E4D8C9] bg-white/80 text-[#6B5E52]',
            )}
            aria-label="Close species summary"
            title="Close"
          >
            ×
          </button>
        ) : null}
      </div>

      <p className={cx('mt-3 line-clamp-2 text-[12.5px] font-semibold leading-5', isDarkMode ? 'text-[#C9D2E1]' : 'text-[#62574D]')}>
        {read.summary}
      </p>

      <div className={cx('mt-3 overflow-hidden rounded-[16px] border', isDarkMode ? 'border-[rgba(132,151,190,0.12)] bg-[#0F1828]' : 'border-[#E9DED2] bg-white/68')}>
        <div className="grid grid-cols-2 divide-x" style={{ borderColor: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(90,70,50,0.09)' }}>
          <div className="px-3 py-3">
            <div className={cx('text-[9px] font-black uppercase tracking-[0.16em]', isDarkMode ? 'text-[#66D9E8]' : 'text-[#1D6D96]')}>Best bet</div>
            <div className={cx('mt-1 text-[12px] font-black leading-4', isDarkMode ? 'text-[#F4F6FA]' : 'text-[#40372F]')}>{read.bestMove}</div>
          </div>
          <div className="px-3 py-3">
            <div className={cx('text-[9px] font-black uppercase tracking-[0.16em]', isDarkMode ? 'text-[#F2B84B]' : 'text-[#8B6400]')}>Throw</div>
            <div className={cx('mt-1 text-[12px] font-black leading-4', isDarkMode ? 'text-[#F4F6FA]' : 'text-[#40372F]')}>{read.bestThrow}</div>
          </div>
        </div>
        {read.bestWindow ? (
          <div className={cx('border-t px-3 py-2.5 text-[11px] font-bold', isDarkMode ? 'border-white/[0.07] text-[#AEB8C9]' : 'border-[#E9DED2] text-[#6A5E52]')}>
            <span className={cx('mr-1 font-black uppercase tracking-[0.12em]', isDarkMode ? 'text-[#C7B9FF]' : 'text-[#2469B2]')}>When</span>
            {read.bestWindow}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onOpenDetail}
        className="mt-3 w-full rounded-[14px] border px-4 py-3 text-[11px] font-black uppercase tracking-[0.14em] leading-4 text-white transition-all hover:-translate-y-[1px] active:translate-y-[1px]"
        style={{
          background: isDarkMode ? '#8A70F2' : '#2F80ED',
          borderColor: 'rgba(255,255,255,0.16)',
          boxShadow: isDarkMode ? '0 8px 18px rgba(40,33,91,0.22)' : '0 12px 24px rgba(18,33,72,0.22)',
        }}
      >
        Open details
      </button>

      {isImageExpanded ? (
        <div
          className="fixed inset-0 z-[140] flex items-center justify-center bg-[#090D18]/88 px-4 py-6 backdrop-blur-sm"
          onClick={() => setIsImageExpanded(false)}
          role="dialog"
          aria-modal="true"
          aria-label={`${read.commonName} reference image`}
        >
          <div
            className={cx(
              'relative w-full max-w-4xl overflow-hidden rounded-[24px] border p-3 shadow-[0_28px_80px_rgba(0,0,0,0.42)]',
              isDarkMode ? 'border-white/10 bg-[#090B13]' : 'border-[#E2D5C6] bg-[rgba(255,251,246,0.98)]',
            )}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsImageExpanded(false)}
              className={cx(
                'absolute right-3 top-3 z-10 grid h-9 w-9 place-items-center rounded-full border text-[14px] font-black transition active:scale-95',
                isDarkMode ? 'border-white/10 bg-white/10 text-[#EDF2FF]' : 'border-[#E4D8C9] bg-white/85 text-[#6B5E52]',
              )}
              aria-label="Close image viewer"
              title="Close"
            >
              ×
            </button>

            <div className="mx-auto flex max-h-[78vh] min-h-[220px] w-full items-center justify-center overflow-hidden rounded-[18px] bg-black/10 p-2">
              <FishIQSpeciesImage
                species={species}
                fallback={<span aria-hidden="true" className="text-[64px]">{icon}</span>}
                size="hero"
                className="h-full max-h-[72vh] w-full max-w-full overflow-hidden rounded-[16px]"
                imageClassName="object-contain bg-transparent"
              />
            </div>

            <div className="mt-3 px-1">
              <div className={cx('truncate text-[16px] font-black', isDarkMode ? 'text-[#FAFBFF]' : 'text-[#332C25]')}>
                {read.commonName}
              </div>
              <div className={cx('mt-1 text-[11px] font-semibold', isDarkMode ? 'text-[#9EABBF]' : 'text-[#766B61]')}>
                Tap outside or press Esc to close.
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </article>
    </>
  );
};

export default FishIQSpeciesCard;
